import type { ProctorClientSettings } from "@/lib/attemptApi";
import { checkDualScreen, watchScreenChanges } from "./dualScreen";
import { scheduleSnapshots, startCamera, watchCameraTrack, type CameraSession } from "./camera";
import { violationOutbox } from "./violationOutbox";

export interface ProctorEvents {
  onViolation?: (type: string, terminal: boolean) => void;
  /** Fired when a rule requires the attempt to end (dual-screen mid-attempt, tab-switch threshold, etc.). */
  onTerminate?: (reason: string) => void;
  /** Fired when the camera track ends mid-attempt — UI should pause and ask the candidate to reconnect. */
  onCameraLost?: () => void;
}

export class ProctorController {
  private stopFns: Array<() => void> = [];
  private cameraStopFns: Array<() => void> = [];
  private camera?: CameraSession;
  private started = false;

  constructor(
    private readonly attemptId: string,
    private readonly settings: ProctorClientSettings,
    private readonly container: HTMLElement,
    private readonly events: ProctorEvents = {},
  ) {}

  /** Preflight — call BEFORE entering the exam view. Rejects if the environment isn't valid. */
  async preflight(): Promise<void> {
    if (this.settings.dualScreenBlock) {
      const s = await checkDualScreen();
      if (!s.isSingle) throw new Error("Please disconnect all additional displays and try again.");
    }
  }

  /** Enter fullscreen, request camera, install listeners. Call after preflight() from a user gesture. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    if (this.settings.fullscreenEnforce) {
      await this.enterFullscreen();
      this.installFullscreenListener();
    }
    if (this.settings.tabSwitchDetect) this.installTabSwitchListener();
    if (this.settings.copyPasteDisable) this.installCopyPasteListener();
    if (this.settings.dualScreenBlock) this.installDualScreenWatcher();
    this.installDevtoolsShortcutBlocker();

    if (this.settings.cameraEnabled) {
      try {
        this.camera = await startCamera();
      } catch (e: any) {
        this.report("CameraDenied", { note: e?.message ?? "User denied camera access" });
        throw new Error("Camera access is required to start this exam. Please allow camera access in your browser and try again.");
      }
      this.attachCameraWatchers();
    }

    // Flush any queued events from previous sessions.
    void this.flushOutbox();
    window.addEventListener("online", this.flushOutbox);
    this.stopFns.push(() => window.removeEventListener("online", this.flushOutbox));
  }

  /**
   * Attempt to reacquire the camera after a mid-exam loss. Resolves true on success,
   * false if the user still hasn't granted access. Fully tears down the previous
   * camera subsystem (stream + snapshot loop + track watcher) before starting a new
   * one — otherwise stale watchers would keep firing onCameraLost() every 3 seconds.
   */
  async restartCamera(): Promise<boolean> {
    if (!this.settings.cameraEnabled) return true;
    this.detachCameraWatchers();
    try {
      this.camera = await startCamera();
    } catch (e: any) {
      this.report("CameraDenied", { note: e?.message ?? "Retry declined" });
      return false;
    }
    this.attachCameraWatchers();
    return true;
  }

  private attachCameraWatchers() {
    if (!this.camera) return;
    const intervalMs = Math.max(
      5_000,
      (this.settings.cameraIntervalMinutes * 60 + this.settings.cameraIntervalSeconds) * 1000,
    );
    this.cameraStopFns.push(scheduleSnapshots({
      attemptId: this.attemptId,
      stream: this.camera.stream,
      intervalMs,
    }));
    this.cameraStopFns.push(watchCameraTrack(this.camera.stream, () => {
      this.report("CameraLost");
      this.events.onCameraLost?.();
    }));
  }

  private detachCameraWatchers() {
    // Remove listeners + intervals FIRST so track.stop() doesn't fire a spurious CameraLost.
    this.cameraStopFns.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    this.cameraStopFns = [];
    this.camera?.stop();
    this.camera = undefined;
  }

  /** Detach all listeners and stop the camera. Idempotent. */
  stop(): void {
    this.stopFns.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    this.stopFns = [];
    this.detachCameraWatchers();
    this.started = false;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }

  // ---- Reporting ----
  report(type: string, metadata?: Record<string, string>) {
    violationOutbox.enqueue(this.attemptId, {
      type: type as any,
      occurredAt: new Date().toISOString(),
      metadata,
    });
    this.events.onViolation?.(type, false);
    void this.flushOutbox();
  }

  private flushOutbox = async () => {
    const { terminal } = await violationOutbox.flush();
    if (terminal) this.events.onTerminate?.("Server marked the attempt as terminated.");
  };

  // ---- Fullscreen ----
  private async enterFullscreen() {
    if (document.fullscreenElement) return;
    try {
      await this.container.requestFullscreen();
    } catch {
      // The browser may reject if not called from a gesture — surface the error to the caller.
      throw new Error("Full-screen was blocked. Click Start Exam again to grant.");
    }
  }

  private installFullscreenListener() {
    const handler = () => {
      if (!document.fullscreenElement) this.report("FullscreenExit");
    };
    document.addEventListener("fullscreenchange", handler);
    this.stopFns.push(() => document.removeEventListener("fullscreenchange", handler));
  }

  // ---- Tab-switch / window-blur ----
  private installTabSwitchListener() {
    const vis = () => {
      if (document.visibilityState === "hidden") this.report("TabSwitch");
    };
    const blur = () => this.report("WindowBlur");
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("blur", blur);
    this.stopFns.push(() => document.removeEventListener("visibilitychange", vis));
    this.stopFns.push(() => window.removeEventListener("blur", blur));
  }

  // ---- Copy/paste/right-click ----
  private installCopyPasteListener() {
    const cancel = (e: Event, type: string) => {
      e.preventDefault();
      this.report(type);
    };
    const c = (e: Event) => cancel(e, "CopyAttempt");
    const p = (e: Event) => cancel(e, "PasteAttempt");
    const r = (e: Event) => cancel(e, "RightClick");
    this.container.addEventListener("copy", c);
    this.container.addEventListener("cut", c);
    this.container.addEventListener("paste", p);
    this.container.addEventListener("contextmenu", r);
    this.stopFns.push(() => {
      this.container.removeEventListener("copy", c);
      this.container.removeEventListener("cut", c);
      this.container.removeEventListener("paste", p);
      this.container.removeEventListener("contextmenu", r);
    });
  }

  // ---- Devtools shortcut deterrent (best-effort) ----
  private installDevtoolsShortcutBlocker() {
    const handler = (e: KeyboardEvent) => {
      const blocked =
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key.toUpperCase())) ||
        (e.ctrlKey && e.key.toUpperCase() === "U");
      if (blocked) {
        e.preventDefault();
        this.report("DevtoolsShortcut", { key: e.key });
      }
    };
    window.addEventListener("keydown", handler);
    this.stopFns.push(() => window.removeEventListener("keydown", handler));
  }

  // ---- Dual-screen mid-attempt watcher ----
  private async installDualScreenWatcher() {
    const cleanup = await watchScreenChanges((s) => {
      if (!s.isSingle) this.report("DualScreenDetected", { screenCount: String(s.screenCount) });
    });
    this.stopFns.push(cleanup);
  }
}
